import { ComponentType, FC } from 'react'
import {
  Redirect,
  RouteProps,
  Route,
  RouteComponentProps,
} from 'react-router-dom'
import { useMeQuery } from '../generated/graphql'
import { Spinner } from './Spinner'
import { can } from '../helpers/hasPermission'
import { routePermissions } from '../helpers/routePermissions'
import { useSelector } from 'react-redux'
import { AppState } from '../redux/rootReducer'
import { AuthState } from '../redux/auth/types'

interface AuthRouteProps {
  component: ComponentType<any>
}

const AuthRoute: FC<AuthRouteProps & RouteProps> = (props) => {
  const { loading, data } = useMeQuery();
  const auth = useSelector<AppState, AuthState>((state) => state.auth);
  
  if (loading) {
    return <Spinner />
  }
  
  if ((data?.me.errors && data.me.errors.length > 0) || !data?.me.user) {
    return <Redirect to="/login" />
  }
  
  // Use Redux state for user data to avoid timing issues
  const user = auth.user || data?.me.user;
  const userPermissions = auth.allPermissions;
  const userRolePermissions = user?.role?.permissions;
  const path = props.path as string;
  
  // Helper function to check if user is admin (case insensitive)
  const isUserAdmin = (roleName?: string) => {
    return roleName?.toLowerCase() === 'admin';
  };
  
  // Handle security routes (admin-only)
  if (path.startsWith('/security/')) {
    if (!isUserAdmin(user?.role?.name)) {
      return <Redirect to="/" />
    }
  }
  
  // Handle routes that require specific permissions
  if (routePermissions[path]) {
    // If permissions haven't been loaded yet, show spinner
    if ((!userPermissions || userPermissions.length === 0) && 
        (!userRolePermissions || userRolePermissions.length === 0)) {
      return <Spinner />
    }
    
    // Check specific permissions
    const requiredPermission = routePermissions[path];
    
    // Check both global permissions and user's role permissions
    const hasGlobalPermission = can(requiredPermission, userPermissions);
    const hasRolePermission = can(requiredPermission, userRolePermissions);
    const hasPermission = hasGlobalPermission || hasRolePermission;
    
    if (!hasPermission) {
      return <Redirect to="/" />
    }
  }

  const { component: _, ...rest } = props
  return (
    <Route
      {...rest}
      render={(routeProps: RouteComponentProps<{}>) => {
        const { component: Component } = props
        return <Component {...routeProps} />
      }}
    />
  )
}
export { AuthRoute };